import { ChangeEventStatus, ChangeEventWorkflow, SaveChangeEventConfigChangeInput } from "src/generated/graphqlTypes";
import {
  baseChangeEventFormConfig,
  BaseChangeEventFormInput,
  baseChangeEventMapToForm,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { SelectOptionsStep } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/1.SelectOptionsStep";
import { SelectedOptionsPane } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/components/SelectedOptionsPane";
import { OptionPriceMode } from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import {
  reconcileImpactedCommitmentsFormConfig,
  ReconcileImpactedCommitmentsFormInput,
  reconcileImpactedCommitmentsMapToForm,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/ReconcileImpactedCommitmentsStep";
import {
  reconcileImpactedCommitmentsStep,
  reviewStep,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/sharedSteps";
import { useGuidedChangeEventActions } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useGuidedChangeEventActions";
import { useMaybeOpenCommitmentCleanupModal } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useMaybeOpenCommitmentCleanupModal";
import { useReviewStepActions } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useReviewStepActions";
import { defineWorkflow } from "src/routes/projects/change-events/guided/workflows/types";
import { idsOf } from "src/utils";
import { ReviewConfigurationStep } from "../applyConfiguration/ReviewConfigurationStep";
import { ReviewImpactStep } from "../sharedSteps/ReviewImpactStep";

export type AddOrModifyOptionsFormInput = BaseChangeEventFormInput & {
  // This workflow doesn't have a target, since its result configuration is the project's current one plus the option
  // changes the user makes, so pull the option ids since they are exhaustive.
  configChange?:
    | (Pick<SaveChangeEventConfigChangeInput, "id" | "optionIds"> & {
        // Local-only fields:
        /** The Draft `configChange.result` config the Select Options step reads its option groups from. */
        resultId?: string | null;
        /** The project's current selection (`configChange.original`), so the step can tell whether anything changed yet. */
        originalOptionIds?: string[] | null;
      })
    | null;
  // Local-only field to track the price mode selected by the user (cost or price).
  priceMode?: OptionPriceMode;
} & ReconcileImpactedCommitmentsFormInput;

export const addOrModifyOptionsWorkflow = defineWorkflow<AddOrModifyOptionsFormInput>({
  workflow: ChangeEventWorkflow.AddOrModifyOptions,
  formConfig: {
    ...baseChangeEventFormConfig,
    priceMode: { type: "value", isLocalOnly: true },
    configChange: {
      type: "object",
      config: {
        id: { type: "value" },
        optionIds: { type: "value" },
        resultId: { type: "value", isLocalOnly: true },
        originalOptionIds: { type: "value", isLocalOnly: true },
      },
    },
    ...reconcileImpactedCommitmentsFormConfig,
  },
  // Creates the Draft change event before the wizard mounts. The project's own (reserved) checkout config only exposes
  // the options it has selected, so the Select Options step can't show alternatives off it. Saving an empty
  // `configChange` makes the backend clone the project's config into the change event's Draft `result`, which does list
  // every option, and that is what the step reads.
  beforeStart: async ({ projectId, input, save }) => {
    const result = await save({
      variables: { structureId: projectId, input: { ...input, status: ChangeEventStatus.Draft, configChange: {} } },
    });
    return { changeEventId: result.data!.saveChangeEvent.changeEvent.id };
  },
  mapToForm: (changeEvent) => {
    const { configChange } = changeEvent;
    return {
      ...baseChangeEventMapToForm(changeEvent),
      priceMode: OptionPriceMode.Price,
      configChange: {
        id: configChange?.id,
        resultId: configChange?.result?.id,
        optionIds: idsOf(configChange?.result?.config.options ?? [], "readyPlanOption"),
        originalOptionIds: idsOf(configChange?.original?.config.options ?? [], "readyPlanOption"),
      },
      ...reconcileImpactedCommitmentsMapToForm(changeEvent),
    };
  },
  steps: [
    {
      value: GuidedChangeEventStep.SelectOptions,
      label: "Select Options",
      isComplete: (form) => {
        const optionIds = form.configChange.optionIds.value ?? [];
        const originalOptionIds = form.configChange.originalOptionIds.value ?? [];
        return optionIds.xor(originalOptionIds).nonEmpty;
      },
      render: (form) => <SelectOptionsStep formState={form} />,
      rightPaneTriggers: (form) => [
        { icon: "list", label: "Changes", content: <SelectedOptionsPane formState={form} /> },
      ],
    },
    {
      value: GuidedChangeEventStep.ReviewConfiguration,
      label: "Review Configuration",
      isComplete: () => true,
      render: (form) => <ReviewConfigurationStep formState={form} />,
    },
    {
      value: GuidedChangeEventStep.ReviewImpact,
      label: "Review Impact",
      isComplete: () => true,
      render: (form) => <ReviewImpactStep formState={form} />,
    },
    reconcileImpactedCommitmentsStep(),
    reviewStep(),
  ],
  useActions: (args) => {
    const { saveChangeEvent } = useGuidedChangeEventActions(args, (form) => {
      const { id, optionIds } = form.configChange.value ?? {};
      return { configChange: { id, optionIds } };
    });
    const { submit } = useReviewStepActions(args);
    const saveAndExit = () => saveChangeEvent({ andExit: true });
    // Adding an option can rewrite scope that's already on a purchase order, so submitting is gated on those being cleaned up by hand first.
    const { submitWithCommitmentCleanup } = useMaybeOpenCommitmentCleanupModal({
      form: args.form,
      onSubmit: submit,
      onSaveDraftAndExit: saveAndExit,
    });
    return {
      onSubmit: submitWithCommitmentCleanup,
      onContinue: { [GuidedChangeEventStep.SelectOptions]: () => saveChangeEvent() },
      onSaveAndExit: saveAndExit,
    };
  },
});
